import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import AppLayout from "@/components/layout/AppLayout";
import { promptCategories, copilotPrompts } from "@/data/copilotData";
import { useDemoState } from "@/data/demoState";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import {
  AlertTriangle, ArrowRight, Send, Copy, CheckCircle2, Clock, Lock,
  Mail, FileText, Truck, Users, Zap, MessageSquare, Sparkles,
  ChevronRight, ExternalLink, Info, Shield, Eye, Search,
  CornerDownLeft, X, Check, Inbox
} from "lucide-react";

const categoryIcons = { blockers: AlertTriangle, 'next-steps': Zap, communication: Mail, readiness: Truck, admin: FileText };
const categoryColors = {
  blockers: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', dot: 'bg-red-500', chip: 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100' },
  'next-steps': { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', dot: 'bg-blue-500', chip: 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100' },
  communication: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', dot: 'bg-emerald-500', chip: 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100' },
  readiness: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', dot: 'bg-amber-500', chip: 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100' },
  admin: { bg: 'bg-slate-100', text: 'text-slate-700', border: 'border-slate-200', dot: 'bg-slate-500', chip: 'bg-slate-100 text-slate-700 border-slate-200 hover:bg-slate-200' },
};
const severityConfig = {
  critical: { border: 'border-l-red-500', bg: 'bg-red-50', text: 'text-red-700', badge: 'bg-red-100 text-red-700' },
  warning: { border: 'border-l-amber-500', bg: 'bg-amber-50', text: 'text-amber-700', badge: 'bg-amber-100 text-amber-700' },
  info: { border: 'border-l-blue-500', bg: 'bg-blue-50', text: 'text-blue-700', badge: 'bg-blue-100 text-blue-700' },
  success: { border: 'border-l-emerald-500', bg: 'bg-emerald-50', text: 'text-emerald-700', badge: 'bg-emerald-100 text-emerald-700' },
};
const urgencyBadge = { critical: 'bg-red-100 text-red-700', high: 'bg-amber-100 text-amber-700', medium: 'bg-blue-100 text-blue-700', low: 'bg-slate-100 text-slate-600' };
const actionTypeIcons = { email: Send, chase: ArrowRight, reminder: Clock, summary: FileText, checklist: CheckCircle2 };

function BlockerRow({ b }) {
  const sev = severityConfig[b.severity] || severityConfig.warning;
  return (
    <div className="flex items-center gap-3 py-2 px-3 rounded-lg bg-white border border-slate-100" data-testid={`blocker-row-${b.conditionRef}`}>
      <div className={`w-2 h-2 rounded-full shrink-0 ${b.severity === 'critical' ? 'bg-red-500 animate-pulse' : 'bg-amber-400'}`} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-900 truncate">{b.title}</p>
        <p className="text-xs text-slate-500">{b.owner} ({b.ownerRole}) · Condition {b.conditionRef}</p>
      </div>
      <div className="text-right shrink-0">
        <Badge className={`text-[10px] ${sev.badge}`}>{b.status}</Badge>
        <p className="text-[10px] text-slate-400 mt-0.5">Due {b.dueDate}</p>
      </div>
    </div>
  );
}

function ResponseCard({ response, onActionClick }) {
  const sev = severityConfig[response.severity] || severityConfig.info;
  return (
    <div className={`rounded-xl border-l-4 ${sev.border} bg-white border border-slate-200 shadow-sm copilot-msg-enter`} data-testid="copilot-response-card">
      <div className="p-5 space-y-4">
        {/* Summary */}
        <div>
          <div className="flex items-start gap-2 mb-1">
            <Badge className={`text-[10px] shrink-0 ${sev.badge}`}>{response.severity}</Badge>
            {response.context && <span className="text-[10px] text-slate-400">{response.context.project} · {response.context.stage}</span>}
          </div>
          <p className="text-sm font-medium text-slate-900 leading-relaxed">{response.summary}</p>
        </div>
        {/* Why it matters */}
        {response.whyItMatters && (
          <div className="bg-slate-50 rounded-lg p-3">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Why this matters</p>
            <p className="text-xs text-slate-600 leading-relaxed">{response.whyItMatters}</p>
          </div>
        )}
        {/* Blockers */}
        {response.blocking?.length > 0 && (
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-2">What is blocking</p>
            <div className="space-y-1.5">{response.blocking.map((b, i) => <BlockerRow key={i} b={b} />)}</div>
          </div>
        )}
        {/* Consequences */}
        {response.consequences && (
          <div className="flex items-start gap-2 bg-red-50/60 rounded-lg p-3">
            <AlertTriangle className="w-3.5 h-3.5 text-red-500 mt-0.5 shrink-0" />
            <p className="text-xs text-red-700 leading-relaxed">{response.consequences}</p>
          </div>
        )}
        {/* Next actions */}
        {response.nextActions?.length > 0 && (
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Recommended next actions</p>
            <div className="space-y-1.5">
              {response.nextActions.map((a) => (
                <div key={a.step} className="flex items-center gap-3 py-1.5 px-3 rounded-lg bg-slate-50/80">
                  <span className="w-5 h-5 rounded-full bg-slate-900 text-white text-[10px] font-bold flex items-center justify-center shrink-0">{a.step}</span>
                  <p className="text-xs text-slate-700 flex-1">{a.action}</p>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-[10px] text-slate-400">{a.owner}</span>
                    <Badge className={`text-[9px] ${urgencyBadge[a.urgency]}`}>{a.urgency}</Badge>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      {/* Action cards + Deep links */}
      {(response.actionCards?.length > 0 || response.deepLinks?.length > 0) && (
        <div className="border-t border-slate-100 p-4 space-y-3">
          {response.actionCards?.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {response.actionCards.map((ac) => {
                const AcIcon = actionTypeIcons[ac.type] || Send;
                return (
                  <button key={ac.id} onClick={() => onActionClick(ac)} data-testid={`action-card-${ac.id}`}
                    className="flex items-center gap-2 px-3 py-2 bg-slate-900 text-white rounded-lg text-xs font-medium hover:bg-slate-800 transition-all active:scale-95 shadow-sm">
                    <AcIcon className="w-3.5 h-3.5" />{ac.title}
                  </button>
                );
              })}
            </div>
          )}
          {response.deepLinks?.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {response.deepLinks.map((dl, i) => (
                <Link key={i} to={dl.path} data-testid={`deep-link-${dl.path.replace(/\//g, '-').slice(1)}`}
                  className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 rounded-lg text-xs font-medium text-slate-600 hover:border-blue-300 hover:text-blue-600 transition-all">
                  <ExternalLink className="w-3 h-3" />{dl.label}
                </Link>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function JobBlockerCopilot() {
  const [messages, setMessages] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [actionLog, setActionLog] = useState([]);
  const [activeAction, setActiveAction] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const [activeCat, setActiveCat] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const { projects, activeProject } = useDemoState();
  const proj = projects.find(p => p.id === activeProject) || projects[0];

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, isTyping]);

  const handlePromptClick = (prompt) => {
    if (isTyping) return;
    setMessages(prev => [...prev, { id: Date.now(), type: 'user', text: prompt.text }]);
    setIsTyping(true);
    setInputValue("");
    setTimeout(() => {
      setMessages(prev => [...prev, { id: Date.now() + 1, type: 'bot', prompt, response: prompt.response }]);
      setIsTyping(false);
    }, 1000 + Math.random() * 600);
  };

  const handleInputSubmit = (e) => {
    e.preventDefault();
    if (!inputValue.trim() || isTyping) return;
    const match = copilotPrompts.find(p => p.text.toLowerCase().includes(inputValue.toLowerCase().slice(0, 20))) ||
      copilotPrompts.find(p => p.category === 'next-steps');
    if (match) {
      handlePromptClick({ ...match, text: inputValue.trim() });
    }
  };

  const handleActionConfirm = (action) => {
    setActionLog(prev => [{ ...action, confirmedAt: new Date().toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' }) }, ...prev]);
    setActiveAction(null);
    toast.success(`${action.title}`, { description: action.recipient ? `Prepared for ${action.recipient}` : 'Action confirmed' });
  };

  const getFollowUps = () => {
    const lastBot = [...messages].reverse().find(m => m.type === 'bot');
    if (!lastBot?.response?.followUps) return [];
    return lastBot.response.followUps.map(id => copilotPrompts.find(p => p.id === id)).filter(Boolean);
  };

  const showWelcome = messages.length === 0;
  const followUps = getFollowUps();
  const displayPrompts = activeCat ? copilotPrompts.filter(p => p.category === activeCat) : copilotPrompts;

  return (
    <AppLayout>
      <div className="flex gap-0 -mx-6 lg:-mx-8 -mt-6" style={{ height: 'calc(100vh - 88px)' }} data-testid="copilot-page">
        {/* ── Left Panel: Context ── */}
        <div className="w-[280px] shrink-0 border-r border-slate-200 bg-white overflow-y-auto" data-testid="copilot-left-panel">
          <div className="p-4 space-y-4">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Active project</p>
              <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                <p className="text-sm font-semibold text-slate-900">{proj.name}</p>
                <p className="text-xs text-slate-500 mt-0.5">{proj.address}</p>
                <div className="flex items-center gap-2 mt-2">
                  <Badge className="text-[10px] bg-slate-100 text-slate-600 border-slate-200">{proj.stage}</Badge>
                  <Badge className="text-[10px] bg-red-100 text-red-700 border-red-200">{proj.blockerCount} blockers</Badge>
                </div>
              </div>
            </div>
            <Separator />
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Stage readiness</p>
              <div className="space-y-1.5">
                {[
                  { stage: 'Pre-Start', state: 'Cleared', color: 'text-emerald-600', icon: CheckCircle2, bg: 'bg-emerald-50' },
                  { stage: 'Pre-Slab', state: 'Blocked', color: 'text-red-600', icon: Lock, bg: 'bg-red-50' },
                  { stage: 'Pre-Frame', state: 'Waiting', color: 'text-slate-400', icon: Clock, bg: 'bg-slate-50' },
                  { stage: 'Pre-OC', state: 'Waiting', color: 'text-slate-400', icon: Clock, bg: 'bg-slate-50' },
                ].map(s => (
                  <div key={s.stage} className={`flex items-center gap-2.5 px-3 py-2 rounded-lg ${s.bg}`}>
                    <s.icon className={`w-3.5 h-3.5 ${s.color}`} />
                    <span className="text-xs font-medium text-slate-700 flex-1">{s.stage}</span>
                    <span className={`text-[10px] font-semibold ${s.color}`}>{s.state}</span>
                  </div>
                ))}
              </div>
            </div>
            <Separator />
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Key people on this job</p>
              <div className="space-y-1.5">
                {[
                  { name: 'David Patel', role: 'Civil Eng.', status: 'Overdue item' },
                  { name: 'Sarah Chen', role: 'Builder PM', status: 'Chasing approval' },
                  { name: 'Mark Stevens', role: 'Surveyor', status: 'Reviewing proof' },
                  { name: 'Tom Bradley', role: 'Planner', status: 'RFI response due' },
                ].map(p => (
                  <div key={p.name} className="flex items-center gap-2 px-2 py-1.5">
                    <div className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center text-[9px] font-semibold text-slate-600 shrink-0">
                      {p.name.split(' ').map(n => n[0]).join('')}
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-slate-700 truncate">{p.name}</p>
                      <p className="text-[10px] text-slate-400">{p.role} · {p.status}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <Separator />
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Trade status</p>
              <div className="flex items-center gap-2 px-3 py-2 bg-red-50 rounded-lg border border-red-100">
                <Truck className="w-4 h-4 text-red-500" />
                <div>
                  <p className="text-xs font-semibold text-red-700">Concreter on hold</p>
                  <p className="text-[10px] text-red-600">Waiting on Pre-Slab clearance</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Center Panel: Conversation ── */}
        <div className="flex-1 flex flex-col min-w-0 bg-slate-50/50" data-testid="copilot-center-panel">
          <ScrollArea className="flex-1">
            <div className="max-w-3xl mx-auto px-6 py-6">
              {showWelcome ? (
                <div className="copilot-msg-enter">
                  {/* Welcome */}
                  <div className="text-center mb-8 pt-8">
                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-slate-900 mb-4 shadow-lg">
                      <Sparkles className="w-7 h-7 text-white" />
                    </div>
                    <h2 className="font-heading font-bold text-2xl text-slate-900 mb-2" data-testid="copilot-welcome-title">Job Blocker Copilot</h2>
                    <p className="text-sm text-slate-500 max-w-md mx-auto">Ask anything about your blockers, readiness, next steps, or draft a message. Grounded in your live project data.</p>
                  </div>
                  {/* Category filter */}
                  <div className="flex flex-wrap justify-center gap-2 mb-6">
                    <button onClick={() => setActiveCat(null)} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${!activeCat ? 'bg-slate-900 text-white' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                      data-testid="copilot-cat-all">All</button>
                    {promptCategories.map(cat => {
                      const CatIcon = categoryIcons[cat.id];
                      const colors = categoryColors[cat.id];
                      return (
                        <button key={cat.id} onClick={() => setActiveCat(activeCat === cat.id ? null : cat.id)}
                          data-testid={`copilot-cat-${cat.id}`}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${activeCat === cat.id ? 'bg-slate-900 text-white' : `bg-white border border-slate-200 text-slate-600 hover:bg-slate-50`}`}>
                          <CatIcon className="w-3 h-3" />{cat.label}
                        </button>
                      );
                    })}
                  </div>
                  {/* Prompt chips */}
                  <div className="space-y-4">
                    {(activeCat ? promptCategories.filter(c => c.id === activeCat) : promptCategories).map(cat => {
                      const colors = categoryColors[cat.id];
                      const catPrompts = copilotPrompts.filter(p => p.category === cat.id);
                      return (
                        <div key={cat.id}>
                          <p className={`text-[10px] font-semibold uppercase tracking-wider ${colors.text} mb-2 px-1`}>{cat.label}</p>
                          <div className="flex flex-wrap gap-2">
                            {catPrompts.map(p => (
                              <button key={p.id} onClick={() => handlePromptClick(p)} data-testid={`copilot-prompt-${p.id}`}
                                className={`px-3 py-2 rounded-lg text-xs font-medium border transition-all active:scale-95 ${colors.chip}`}>
                                {p.text}
                              </button>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {messages.map(msg => (
                    msg.type === 'user' ? (
                      <div key={msg.id} className="flex justify-end copilot-msg-enter">
                        <div className="bg-slate-900 text-white rounded-2xl rounded-br-sm px-4 py-2.5 max-w-md shadow-sm" data-testid="copilot-user-msg">
                          <p className="text-sm">{msg.text}</p>
                        </div>
                      </div>
                    ) : (
                      <div key={msg.id} className="copilot-msg-enter">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-6 h-6 rounded-lg bg-slate-900 flex items-center justify-center">
                            <Sparkles className="w-3.5 h-3.5 text-white" />
                          </div>
                          <span className="text-xs font-medium text-slate-500">Copilot</span>
                        </div>
                        <ResponseCard response={msg.response} onActionClick={setActiveAction} />
                      </div>
                    )
                  ))}
                  {isTyping && (
                    <div className="flex items-center gap-2 copilot-msg-enter" data-testid="copilot-typing">
                      <div className="w-6 h-6 rounded-lg bg-slate-900 flex items-center justify-center">
                        <Sparkles className="w-3.5 h-3.5 text-white" />
                      </div>
                      <div className="flex gap-1 px-4 py-3 bg-white rounded-2xl border border-slate-200">
                        <span className="w-2 h-2 rounded-full bg-slate-400 typing-dot" style={{ animationDelay: '0s' }} />
                        <span className="w-2 h-2 rounded-full bg-slate-400 typing-dot" style={{ animationDelay: '0.15s' }} />
                        <span className="w-2 h-2 rounded-full bg-slate-400 typing-dot" style={{ animationDelay: '0.3s' }} />
                      </div>
                    </div>
                  )}
                  {/* Follow-up prompts */}
                  {!isTyping && followUps.length > 0 && (
                    <div className="pt-2 copilot-msg-enter" data-testid="copilot-followups">
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Suggested follow-ups</p>
                      <div className="flex flex-wrap gap-2">
                        {followUps.map(p => {
                          const colors = categoryColors[p.category];
                          return (
                            <button key={p.id} onClick={() => handlePromptClick(p)} data-testid={`copilot-followup-${p.id}`}
                              className={`px-3 py-2 rounded-lg text-xs font-medium border transition-all active:scale-95 ${colors.chip}`}>
                              {p.text}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </div>
          </ScrollArea>
          {/* Input bar */}
          <div className="border-t border-slate-200 bg-white px-6 py-3">
            <form onSubmit={handleInputSubmit} className="max-w-3xl mx-auto flex items-center gap-2">
              <div className="flex-1 relative">
                <input ref={inputRef} type="text" value={inputValue} onChange={e => setInputValue(e.target.value)}
                  placeholder="Ask about blockers, readiness, next steps, or draft a message..."
                  data-testid="copilot-input"
                  className="w-full h-11 pl-4 pr-12 rounded-xl border border-slate-200 bg-slate-50 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-300 transition-all" />
                <button type="submit" disabled={isTyping || !inputValue.trim()} data-testid="copilot-send-btn"
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-lg bg-slate-900 text-white flex items-center justify-center hover:bg-slate-800 disabled:opacity-30 disabled:hover:bg-slate-900 transition-all">
                  <CornerDownLeft className="w-3.5 h-3.5" />
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* ── Right Panel: Action Log ── */}
        <div className="w-[260px] shrink-0 border-l border-slate-200 bg-white overflow-y-auto" data-testid="copilot-right-panel">
          <div className="p-4 space-y-4">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Actions taken</p>
              {actionLog.length === 0 ? (
                <div className="text-center py-6">
                  <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center mx-auto mb-2">
                    <Inbox className="w-5 h-5 text-slate-300" />
                  </div>
                  <p className="text-xs text-slate-400">No actions taken yet</p>
                  <p className="text-[10px] text-slate-300 mt-0.5">Confirmed actions appear here</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {actionLog.map((a, i) => (
                    <div key={i} className="bg-emerald-50 border border-emerald-100 rounded-lg p-2.5" data-testid={`action-log-${i}`}>
                      <div className="flex items-center gap-1.5 mb-1">
                        <Check className="w-3 h-3 text-emerald-600" />
                        <span className="text-[10px] font-semibold text-emerald-700">Confirmed</span>
                        <span className="text-[10px] text-emerald-500 ml-auto">{a.confirmedAt}</span>
                      </div>
                      <p className="text-xs font-medium text-slate-700">{a.title}</p>
                      {a.recipient && <p className="text-[10px] text-slate-500">To: {a.recipient}</p>}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <Separator />
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Quick stats</p>
              <div className="space-y-2">
                {[
                  { label: 'Active blockers', value: '7', color: 'text-red-600' },
                  { label: 'Stages not ready', value: '3', color: 'text-red-600' },
                  { label: 'Proof awaiting review', value: '6', color: 'text-amber-600' },
                  { label: 'Projects at risk', value: '2', color: 'text-red-600' },
                ].map(s => (
                  <div key={s.label} className="flex items-center justify-between px-2 py-1.5">
                    <span className="text-xs text-slate-500">{s.label}</span>
                    <span className={`text-sm font-bold ${s.color}`}>{s.value}</span>
                  </div>
                ))}
              </div>
            </div>
            <Separator />
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Quick links</p>
              <div className="space-y-1">
                {[
                  { label: 'Stage Readiness', path: '/builder/stage-readiness', icon: Lock },
                  { label: 'Proof Review', path: '/builder/proof-review', icon: Eye },
                  { label: 'Trade Release', path: '/builder/trade-release', icon: Truck },
                  { label: 'Council Requests', path: '/builder/rfi-war-room', icon: FileText },
                ].map(l => (
                  <Link key={l.path} to={l.path} data-testid={`copilot-quick-${l.path.split('/').pop()}`}
                    className="flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors">
                    <l.icon className="w-3.5 h-3.5 text-slate-400" />{l.label}
                    <ChevronRight className="w-3 h-3 text-slate-300 ml-auto" />
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Action Confirmation Sheet ── */}
      <Sheet open={!!activeAction} onOpenChange={() => setActiveAction(null)}>
        <SheetContent side="right" className="w-[480px] sm:max-w-[480px] flex flex-col" data-testid="copilot-action-sheet">
          {activeAction && (
            <>
              <SheetHeader>
                <div className="flex items-center gap-2 mb-1">
                  {(() => { const AcIcon = actionTypeIcons[activeAction.type] || Send; return <div className="w-8 h-8 rounded-lg bg-slate-900 flex items-center justify-center"><AcIcon className="w-4 h-4 text-white" /></div>; })()}
                  <div>
                    <SheetTitle className="text-base">{activeAction.title}</SheetTitle>
                    <SheetDescription>{activeAction.description}</SheetDescription>
                  </div>
                </div>
              </SheetHeader>
              <div className="flex-1 mt-4 space-y-4 overflow-y-auto">
                {activeAction.recipient && (
                  <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-100">
                    <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-xs font-semibold text-slate-600">
                      {activeAction.recipient.split(' ').map(n => n[0]).join('').slice(0, 2)}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-900">{activeAction.recipient}</p>
                      <p className="text-xs text-slate-500">{activeAction.recipientRole}</p>
                    </div>
                  </div>
                )}
                <div className="bg-slate-50 rounded-lg border border-slate-200 p-4">
                  <pre className="text-xs text-slate-700 whitespace-pre-wrap font-body leading-relaxed" data-testid="action-sheet-content">
                    {activeAction.content}
                  </pre>
                </div>
              </div>
              <SheetFooter className="mt-4 flex gap-2 sm:flex-row">
                <Button variant="outline" size="sm" data-testid="action-sheet-copy"
                  onClick={() => { navigator.clipboard?.writeText(activeAction.content); toast.success('Copied to clipboard'); }}
                  className="flex items-center gap-1.5">
                  <Copy className="w-3.5 h-3.5" />Copy
                </Button>
                <Button size="sm" data-testid="action-sheet-confirm"
                  onClick={() => handleActionConfirm(activeAction)}
                  className="flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800">
                  <Check className="w-3.5 h-3.5" />Approve & Send
                </Button>
              </SheetFooter>
            </>
          )}
        </SheetContent>
      </Sheet>
    </AppLayout>
  );
}
